<template>
  <!-- S1190-->
  <VSpinner v-if="isLoading" class="offset-top-2" />
  <article v-else>
    <VForm :form="form" @submit="onNextStep">
      <div class="formdata">
        <h3 v-html="$t('payment.auth_data_headline')" />
        <VServiceDisplay :selected-service="serviceId" />
        <VAutoRows>
          <VInput
            v-if="serviceData.unitsEnabled"
            name="units"
            :label="$t('payment.auth_data_initial_units_label')"
            required
            :placeholder="$t('payment.auth_data_initial_units_placeholder_text')"
            :model-value="formValues.units"
            hint="payment.error_text_payment_generation_only_numbers_allowed"
          />
          <div v-if="showTimeUnits" class="form-input-set effort">
            <VInput
              class="offset-right-1"
              name="hours"
              :label="$t('payment.auth_data_hours_label')"
              :placeholder="$t('payment.auth_data_hours_placeholder')"
              type="number"
              :model-value="formValues.hours"
            />
            <VInput
              name="minutes"
              :label="$t('payment.auth_data_minutes_label')"
              :placeholder="$t('payment.auth_data_minutes_placeholder')"
              required
              type="number"
              :model-value="formValues.minutes"
            />
          </div>
          <VInput
            v-if="serviceData.serviceDescriptionEnabled"
            name="serviceDescription"
            :label="$t('payment.auth_data_external_description_label')"
            :placeholder="$t('payment.auth_data_external_description_placeholder')"
            :model-value="formValues.serviceDescription"
          />
          <VInput
            v-if="serviceData.commentEnabled"
            name="commentField"
            :label="$t('payment.auth_data_external_comment_label')"
            :placeholder="$t('payment.auth_data_external_comment_placeholder')"
            :model-value="formValues.commentField"
          />
          <VSelect
            v-for="key in Object.keys(serviceData.descriptions)"
            :key="key"
            :name="key"
            :model-value="formValues[key]"
            :label="'payment.select_description_' + key.toLowerCase()"
            :placeholder="'payment.select_placeholder_' + key.toLowerCase()"
            required
            hint="payment.error_text_payment_generation_empty_selection"
          />
        </VAutoRows>
        <div class="offset-top-2 pull-right">
          <VButton type="button" label="payment.progress_button_previous_step" class="btn-default offset-right-1" @click="onPreviousStep" />
          <VButton type="submit" label="payment.progress_button_next_step" class="btn-default btn-brand " />
        </div>
      </div>
    </VForm>
  </article>
</template>

<script>
import VSpinner from '@/components/Spinner';
import VInput from '@/components/Form/Input';
import VForm from '@/components/Form/Form';
import VSelect from '@/components/Form/Select';
import VButton from '@/components/Button';
import VAutoRows from '@/components/AutoRows';
import { PAYMENT_GENERATION, PAYMENT_GENERATION_SUMMARY } from '@/router/route-types';
import {
  focusFirstError,
  getFormValues,
  hasMaxLength,
  hasMaxLengthOrEmpty,
  hasMinLength,
  initFields,
  isNumberBetweenInclusive,
  isNumberBetweenInclusiveOrEmpty,
  match,
} from '@/utils/form';
import { mapActions, mapState } from 'vuex';
import VServiceDisplay from '@/components/PaymentGeneration/ServiceDisplay';

export default {
  name: 'ProductData',

  components: {
    VSpinner,
    VInput,
    VForm,
    VSelect,
    VButton,
    VAutoRows,
    VServiceDisplay,
  },

  data() {
    return {
      isLoading: true,
      form: null,
    };
  },

  computed: {
    showTimeUnits() {
      return this.serviceData.effortEnabled && !this.serviceData.unitsEnabled;
    },

    ...mapState('payment', ['serviceData', 'serviceId', 'formValues']),
  },

  created() {
    const serviceDescriptionField = this.serviceData.serviceDescriptionEnabled
      ? [
          {
            name: 'serviceDescription',
            validators: [
              {
                handler: hasMaxLengthOrEmpty(40),
                hint: 'payment.error_text_payment_generation_max_length_40',
              },
            ],
          },
        ]
      : [];

    const inputCommentField = this.serviceData.commentEnabled
      ? [
          {
            name: 'commentField',
            validators: [
              {
                handler: hasMaxLengthOrEmpty(80),
                hint: 'payment.error_text_payment_generation_max_length_80',
              },
            ],
          },
        ]
      : [];

    const timeUnits = this.showTimeUnits
      ? [
          {
            name: 'minutes',
            validators: [
              {
                handler: hasMinLength(1),
                hint: 'payment.error_text_required',
              },
              {
                handler: isNumberBetweenInclusive(0, 59),
                hint: 'payment.error_text_minutes',
              },
            ],
          },
          {
            name: 'hours',
            validators: [
              {
                handler: isNumberBetweenInclusiveOrEmpty(0, 99),
                hint: 'payment.error_text_hours',
              },
            ],
          },
        ]
      : [];

    const descriptions = this.serviceData.descriptions;
    const descriptionFields = Object.keys(descriptions).map(name => ({
      name,
      items: descriptions[name].map(id => ({ id, label: id })),
    }));

    const unitField = this.serviceData.unitsEnabled
      ? [
          {
            name: 'units',
            validators: [
              hasMinLength(1),
              match(/^[+]?[1-9][0-9]*$/),
              {
                handler: hasMaxLength(10),
                hint: 'payment.error_text_payment_generation_max_length_10',
              },
            ],
          },
        ]
      : [];
    this.form = initFields(...unitField, ...timeUnits, ...serviceDescriptionField, ...inputCommentField, ...descriptionFields);
    this.isLoading = false;
  },

  methods: {
    async onNextStep({ form, isValid }) {
      const formValues = getFormValues(this.form);
      if (this.serviceData.effortEnabled && parseInt(formValues.minutes) === 0 && (!formValues.hours || parseInt(formValues.hours) === 0)) {
        form.minutes.setInvalid(this.$to('payment.error_text_at_least_one_minute'));
        focusFirstError(form);
      } else if (isValid) {
        if (!formValues.hours) formValues.hours = '0';
        this.setFormValues(formValues);
        this.setValidForm(true);
        this.isLoading = true;
        await this.callXtcAuth();
        await this.$router.push({
          name: PAYMENT_GENERATION_SUMMARY,
        });
      }
    },

    onPreviousStep() {
      this.setValidForm(false);
      this.setFormValues(getFormValues(this.form));
      this.$router.push({ name: PAYMENT_GENERATION });
    },
    ...mapActions('payment', ['setFormValues', 'setValidForm', 'callXtcAuth']),
  },
};
</script>

<style scoped lang="scss">
@import '~@scss';

@mixin no-line {
  padding-top: 0;
  border-top: none;
}

::v-deep {
  .cap-form {
    .cap-form-fieldset,
    .cap-form-fieldset label {
      margin-bottom: 0;
    }
  }

  .formdata {
    > .row > div > .form-input-set {
      padding-top: $building-unit / 2 - 1px;
      padding-bottom: $building-unit / 2;
      border-top: 1px solid $color-border-bright;

      label {
        font-size: $font-size-regular;

        :not(.text-negative) {
          font-weight: $font-weight-semibold;
        }
      }
    }

    .effort {
      display: flex;
      margin-bottom: 0;
    }

    > .row:first-of-type {
      > div:first-of-type .form-input-set {
        @include no-line();
      }

      @media screen and (min-width: #{$grid-medium-max}px) {
        .form-input-set {
          @include no-line();
        }
      }
    }
  }
}
</style>
